import type { FeatureModule, FeatureState } from '../types';

interface SpacingValues {
  lineHeight: number;
  paragraphSpacing: number;
  letterSpacing: number;
  wordSpacing: number;
}

// WCAG 1.4.12 nennt als Mindestwerte Zeilenhöhe 1.5, Buchstabenabstand 0.12em,
// Wortabstand 0.16em, Absatzabstand 2em. Für einen spürbaren „mehr Abstand"-
// Schalter starten wir die Zeilenhöhe etwas großzügiger bei 1.8 (Rest auf den
// WCAG-Werten); der Slider kann von hier bis MAX hochgehen.
const DEFAULT_VALUES: SpacingValues = {
  lineHeight: 1.8,
  paragraphSpacing: 2,
  letterSpacing: 0.12,
  wordSpacing: 0.16,
};

const MAX_VALUES: SpacingValues = {
  lineHeight: 2.5,
  paragraphSpacing: 4,
  letterSpacing: 0.24,
  wordSpacing: 0.32,
};

export default function createSpacingModule(): FeatureModule {
  let enabled = false;
  let currentValues: SpacingValues = { ...DEFAULT_VALUES };
  const STYLE_ID = 'accessify-spacing';
  const STORAGE_KEY = 'accessify-spacing';

  function applySpacing(values: SpacingValues) {
    const root = document.documentElement;
    root.style.setProperty('--accessify-line-height', String(values.lineHeight));
    root.style.setProperty('--accessify-paragraph-spacing', `${values.paragraphSpacing}em`);
    root.style.setProperty('--accessify-letter-spacing', `${values.letterSpacing}em`);
    root.style.setProperty('--accessify-word-spacing', `${values.wordSpacing}em`);

    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    // Widget-Host UND Accessify-Light-DOM-Overlays (Masken, TTS-Leiste, Lupe,
    // Skip-Link, Toast — alle mit id="accessify-…") ausnehmen: `*` mit
    // !important schlug sonst die :host-Resets und verzerrte das eigene Panel
    // genau in dem Moment, in dem der Besucher die Funktion nutzt.
    const EX =
      ':not(#accessify-root):not(#accessify-root *)' +
      ':not([id^="accessify-"]):not([id^="accessify-"] *)';
    styleEl.textContent = `
      /* accessify WCAG 1.4.12 text spacing adjustments */
      :where(*)${EX} {
        line-height: var(--accessify-line-height) !important;
        letter-spacing: var(--accessify-letter-spacing) !important;
        word-spacing: var(--accessify-word-spacing) !important;
      }
      p${EX} {
        margin-bottom: var(--accessify-paragraph-spacing) !important;
      }
      p + p${EX} {
        margin-top: var(--accessify-paragraph-spacing) !important;
      }
    `;
  }

  function removeSpacing() {
    const root = document.documentElement;
    root.style.removeProperty('--accessify-line-height');
    root.style.removeProperty('--accessify-paragraph-spacing');
    root.style.removeProperty('--accessify-letter-spacing');
    root.style.removeProperty('--accessify-word-spacing');
    const styleEl = document.getElementById(STYLE_ID);
    styleEl?.remove();
  }

  function clampValues(values: Partial<SpacingValues>): SpacingValues {
    return {
      lineHeight: Math.min(MAX_VALUES.lineHeight, Math.max(DEFAULT_VALUES.lineHeight, values.lineHeight ?? currentValues.lineHeight)),
      paragraphSpacing: Math.min(MAX_VALUES.paragraphSpacing, Math.max(DEFAULT_VALUES.paragraphSpacing, values.paragraphSpacing ?? currentValues.paragraphSpacing)),
      letterSpacing: Math.min(MAX_VALUES.letterSpacing, Math.max(DEFAULT_VALUES.letterSpacing, values.letterSpacing ?? currentValues.letterSpacing)),
      wordSpacing: Math.min(MAX_VALUES.wordSpacing, Math.max(DEFAULT_VALUES.wordSpacing, values.wordSpacing ?? currentValues.wordSpacing)),
    };
  }

  function activate() {
    enabled = true;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        currentValues = clampValues(JSON.parse(saved));
      } catch {
        currentValues = { ...DEFAULT_VALUES };
      }
    } else {
      currentValues = { ...DEFAULT_VALUES };
    }
    applySpacing(currentValues);
  }

  function deactivate() {
    enabled = false;
    removeSpacing();
    localStorage.removeItem(STORAGE_KEY);
    currentValues = { ...DEFAULT_VALUES };
  }

  return {
    id: 'spacing',
    name: () => 'Text Spacing',
    description: 'Adjust line height, letter spacing, word spacing and paragraph spacing (WCAG 1.4.12)',
    icon: 'spacing',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'spacing',
      enabled,
      value: { ...currentValues },
    }),
    setState: (values: Partial<SpacingValues>) => {
      currentValues = clampValues(values);
      applySpacing(currentValues);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentValues));
      enabled = true;
    },
  };
}
